import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { Form, Row, Col } from "react-bootstrap";
import { IoCloseOutline } from "react-icons/io5";
import CommonModal from "../../../utils/commonModal";
import DesktopDatePicker from "@mui/lab/DatePicker";
import AdapterDateFns from '@mui/lab/AdapterDateFns'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import { format, isValid, parse, isToday, isBefore, startOfDay } from 'date-fns';
import Select, { components } from 'react-select';
import { Alert } from "@mui/material";
// import Avatar from 'react-avatar';
import { FiEdit, FiClock } from "react-icons/fi";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { Box } from '@mui/material';
import { LuInfo, LuCalendar } from "react-icons/lu";
import { FiUsers } from "react-icons/fi";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import CustomDropdown from './customDropdown';
import { getScheduledDraft, putScheduledDraft } from '../../../../../store/actions/cms/scheduler';
import { clearData } from "../../../../../store/slices/scheduler";
import CreatableSelect from "react-select/creatable";

const SetSchedulePopup = ({ customDate, customTime, setCustomDate, setCustomTime, ...props }) => {
    const dispatch = useDispatch();
    const selectRef = useRef();
    const [dateOpen, setDateOpen] = useState(false);
    const [users, setUsers] = useState([]);
    const [toBeRemoved, setToBeRemoved] = useState(null);
    const [errors, setErrors] = useState({ customDate: '', customTime: '' });
    const [hasPrefilled, setHasPrefilled] = useState(false);

    const { scheduleDraftsData, authorEmailData, putScheduleDraftsData, putScheduleDraftsError } = useSelector((state) => { return (state?.contentSchedularReducer) });
    const currentUser = useSelector((state) => state?.userData?.user?.data);

    const isScheduleDisabled = !customDate || !!errors.customDate || !customTime || !!errors.customTime;
    const [showAll, setShowAll] = useState(false);
    const userLimit = 20;

    useEffect(() => {
        if (props.open) {
            dispatch(getScheduledDraft({ 'id': props.open?.id }))
        }
    }, [props.open])

    // to close the popup
    useEffect(() => {
        if(putScheduleDraftsData)props.setOpen(null)
    }, [putScheduleDraftsData])
    
    // to show prefilled form data
    useEffect(() => {
        if (scheduleDraftsData && props.open) {
            // setting date as prefilled
            const scheduleDate = scheduleDraftsData?.scheduled_publish_at 
                ? new Date(scheduleDraftsData.scheduled_publish_at) 
                : null;
            setCustomDate(scheduleDate);
            
            // setting time as prefilled
            const timeString = getTimeFromISODateTime(scheduleDraftsData?.scheduled_publish_at);
            const existingOption = timeOptions.find(opt => opt.value === timeString);
            const timeValue = existingOption || (timeString ? { label: timeString, value: timeString } : "");
            setCustomTime(timeValue);
            
            // setting users as prefilled
            const users_ = scheduleDraftsData.notification_recipient || [];
            setUsers(Array.isArray(users_) ? users_ : []);
            setHasPrefilled(true);
        }
    }, [scheduleDraftsData, props.open]); // Add props.open as dependency

    const visibleUsers = showAll ? users : (Array.isArray(users) ? users.slice(0, 3) : []);
    const hiddenCount = Array.isArray(users) ? users.length - 3 : 0;

    // function to check if a time is in the past for today's date
    const isTimeInPast = (timeValue, selectedDate) => {
        if (!timeValue || !selectedDate) return false;
        
        const now = new Date();
        
        // Only check if the selected date is today
        if (!isToday(selectedDate)) return false;
        
        // Parse the time string
        const [timePart, meridian] = timeValue.split(" ");
        let [hours, minutes] = timePart.split(":").map(Number);
        
        // Convert to 24-hour format
        if (meridian === "PM" && hours !== 12) hours += 12;
        if (meridian === "AM" && hours === 12) hours = 0;
        
        // Create a date object for the selected time today
        const selectedDateTime = new Date();
        selectedDateTime.setHours(hours, minutes, 0, 0);
        
        // Check if the selected time is in the past (with a 1-minute buffer)
        const nowPlusBuffer = new Date(now.getTime() + 60000); // Add 1 minute buffer
        return selectedDateTime < nowPlusBuffer;
    };

    // validation
    useEffect(() => {
        if (!hasPrefilled) return;

        let newErrors = { customDate: "", customTime: "", user: "" };
        let parsedDate = null;

        // Date validation
        if (!customDate) {
            newErrors.customDate = "Please select a date.";
        } else {
            if (typeof customDate === "string") {
                // Trying to parse user-typed string with inputFormat
                parsedDate = parse(customDate, "EEEE, d MMMM", new Date());
            } else if (customDate instanceof Date && isValid(customDate)) {
                parsedDate = customDate;
            }

            if (!parsedDate || !isValid(parsedDate)) {
                newErrors.customDate = "Invalid date format. Expected: EEEE, d MMMM";
            } else {
                const today = startOfDay(new Date());
                const selectedDate = startOfDay(parsedDate);

                if (isBefore(selectedDate, today)) {
                    newErrors.customDate = "Date cannot be in the past.";
                }
            }
        }

        // Time validation (including combined date-time validation)
        const timeValue = typeof customTime === "string" ? customTime : customTime?.value || "";
        if (!timeValue) {
            newErrors.customTime = "Please select a time.";
        } else {
            const timeRegex = /^(0[1-9]|1[0-2]):[0-5][0-9] (AM|PM)$/;
            if (!timeRegex.test(timeValue)) {
                newErrors.customTime = "Format error: HH:MM AM or HH:MM PM";
            } else if (parsedDate && isTimeInPast(timeValue, parsedDate)) {
                newErrors.customTime = "Selected time cannot be in the past for today's date.";
            }
        }

        // User validation
        if (users?.length >= userLimit) {
            newErrors.user = `You can select a maximum of ${userLimit} users.`;
        }

        setErrors(newErrors);
    }, [customDate, customTime, users, hasPrefilled]);

    // clear error message after 3 sec (PUT API)
    useEffect(() => {
        if (putScheduleDraftsError) {
            setTimeout(() => {
                dispatch(clearData())
            }, 3000);
        }
    }, [putScheduleDraftsError]);

    const showTooltip = (alertMsg) => {
        return (
            <Tooltip id="scheduler_popup" className="popup-tooltip">
                <div className="tooltip-text">{alertMsg}</div>
            </Tooltip>
        );
    };

    // Enhanced time options generation - filter past times for today
    const generateTimeOptions = () => {
        const times = [];
        const now = new Date();
        const isSelectedDateToday = customDate && isToday(customDate);

        for (let hour = 0; hour < 24; hour++) {
            // showing options for 1 min interval
            for (let min = 0; min < 60; min += 1) {
                const period = hour < 12 ? 'AM' : 'PM';
                const hour12 = hour % 12 === 0 ? 12 : hour % 12;
                const formattedHour = hour12.toString().padStart(2, '0');
                const formattedMin = min.toString().padStart(2, '0');
                const timeString = `${formattedHour}:${formattedMin} ${period}`;

                // If selected date is today, filter out past times
                if (isSelectedDateToday) {
                    const timeDate = new Date();
                    timeDate.setHours(hour, min, 0, 0);
                    const nowPlusBuffer = new Date(now.getTime() + 60000); // 1 minute buffer

                    if (timeDate >= nowPlusBuffer) {
                        times.push({ label: timeString, value: timeString });
                    }
                } else {
                    times.push({ label: timeString, value: timeString });
                }
            }
        }
        return times;
    };

    const timeOptions = generateTimeOptions();

    // to show data prefilled
    const getTimeFromISODateTime = (isoString) => {
        if (!isoString) return "";

        const date = new Date(isoString);
        let hours = date.getHours();
        const minutes = date.getMinutes();
        const meridian = hours >= 12 ? "PM" : "AM";

        hours = hours % 12 || 12; // convert 0 -> 12 for 12 AM
        const formattedHour = hours.toString().padStart(2, "0");
        const formattedMin = minutes.toString().padStart(2, "0");

        return `${formattedHour}:${formattedMin} ${meridian}`;
    };

    // Enhanced date change handler
    const handleDateChange = (newDate) => {
        setCustomDate(newDate);

        // If changing to today's date and current time is in the past, clear the time
        if (newDate && isToday(newDate) && customTime) {
            const timeValue = typeof customTime === "string" ? customTime : customTime?.value || "";
            if (isTimeInPast(timeValue, newDate)) {
                setCustomTime(null);
            }
        }
    };

    // Enhanced time change handler
    const handleTimeChange = (selectedOption) => {
        setCustomTime(selectedOption);

        if (selectRef.current) {
            selectRef.current.blur();
        }
    };

    //   to combine date and time field
    /*
        customDate = "2025-08-01"
        customTime = "02:15 PM"
        getCombinedISODateTime(); // → "2025-08-01T14:15:00.000Z"
    */
    const getCombinedISODateTime = () => {
        if (!customDate || !customTime) return null;

        // Extract string value if customTime is an object
        const timeValue = typeof customTime === "string" ? customTime : customTime.value;

        const [timePart, meridian] = timeValue.split(" ");
        let [hours, minutes] = timePart.split(":").map(Number);

        // Adjust the hour based on AM/PM
        if (meridian === "PM" && hours !== 12) hours += 12;
        if (meridian === "AM" && hours === 12) hours = 0;

        // Extract only the date (without the time) from customDate
        const customDateOnly = new Date(customDate);
        customDateOnly.setHours(0, 0, 0, 0); // Reset to midnight (start of the day)

        // Combine the extracted date with the new time
        customDateOnly.setHours(hours);
        customDateOnly.setMinutes(minutes);
        customDateOnly.setSeconds(0);
        customDateOnly.setMilliseconds(0);

        // Get the timezone offset in hours and minutes (e.g., UTC+5:30)
        const offset = customDateOnly.getTimezoneOffset(); // Timezone offset in minutes
        const offsetHours = Math.floor(Math.abs(offset) / 60);
        const offsetMinutes = Math.abs(offset) % 60;
        const sign = offset > 0 ? "-" : "+";

        // Format the offset in `+hh:mm` or `-hh:mm`
        const timezoneOffset = `${sign}${String(offsetHours).padStart(2, "0")}:${String(offsetMinutes).padStart(2, "0")}`;

        // Manually construct the ISO string with the local timezone offset
        const year = customDateOnly.getFullYear();
        const month = String(customDateOnly.getMonth() + 1).padStart(2, "0");
        const day = String(customDateOnly.getDate()).padStart(2, "0");
        const hoursFormatted = String(customDateOnly.getHours()).padStart(2, "0");
        const minutesFormatted = String(customDateOnly.getMinutes()).padStart(2, "0");
        const secondsFormatted = String(customDateOnly.getSeconds()).padStart(2, "0");

        // Combine into the final formatted string: YYYY-MM-DDTHH:MM:SS+hh:mm
        const combinedDateTime = `${year}-${month}-${day}T${hoursFormatted}:${minutesFormatted}:${secondsFormatted}${timezoneOffset}`;

        return combinedDateTime;
    };

    const CustomControl = ({ children, type, ...props }) => {
        return (
            <components.Control {...props}>
                <div className='d-flex justify-content-between' style={{ width: '100%', color: '#606060 !important' }}>
                    {type !== 'user' ?
                        <FiClock size={18} className="time_field_clock_icon" /> :
                        <FiUsers size={18} className="time_field_clock_icon" />
                    }
                    {children}
                </div>
            </components.Control>
        );
    };

    const payload = (status) => {
        const notification_recipients = users?.map(item => ({ user_id: item.user_id }));
        let data = {
            "id": props.open?.id,
            "task_status": status,
            "scheduled_by": currentUser?.id || 1,
        }
        if (status == 'rescheduled') {
            data.scheduled_publish_at = getCombinedISODateTime(),
                data.notification_recipients = notification_recipients
        }
        return data;
    }

    const handleRemove = () => {
        const data = payload('cancelled')
        dispatch(putScheduledDraft(data));
    }

    const handleSchedule = () => {
        const data = payload('rescheduled')
        dispatch(putScheduledDraft(data));
    }

    // customised css for time dropdown
    const customStyles = {
        control: (provided, state) => ({
            ...provided,
            backgroundColor: state.isFocused ? '#fff' : '#f3f6fb',
            borderRadius: '100px',
            border: state.isFocused ? '1px solid #2684ff' : 'none',
            boxShadow: 'none',
            cursor: 'pointer',
            color: '#606060 !important'
        }),
        menu: (provided) => ({
            ...provided,
            borderRadius: '6px',
            boxShadow: '0 0 12px 0 rgba(147, 153, 190, 0.2)',
            zIndex: 9999,
            border: 'solid 1px #dce8fa',
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isFocused ? '#eef0ff' : state.isSelected ? '#eef0ff' : '#fff',
            color: state.isFocused ? '#48569a' : '#474747',
            padding: '10px 12px',
            cursor: 'pointer',
            borderRadius: '6px',
            border: 'none'
        }),
        dropdownIndicator: (provided) => ({
            ...provided,
            color: '#999',
            padding: '8px',
        }),
        indicatorSeparator: () => ({
            display: 'none',
        }),
        valueContainer: (provided) => ({
            ...provided,
            padding: '0 12px',
        }),
        menuList: (provided) => ({
            ...provided,
            maxHeight: '272px',
            overflowY: 'auto',
            padding: '12px',
        }),
    };

    return (
        <>
            <CommonModal
                isOpen={props.open}
                closeModal={() => {
                    props.setOpen(false);
                }}
                showCloseIcon={false}
                closeOnOverlayClick={false}
                classNames={{ root: "schedule_modal", overlay: "schedule_overlay", button: "scheduleButton" }}
            >

                <div className='py-3 px-3 schedule_popup'>
                    <div className="d-flex justify-content-end">
                        <OverlayTrigger
                            placement="bottom"
                            trigger={['hover']}
                            overlay={showTooltip('Edit Version')}
                            popperConfig={{
                                modifiers: [
                                    {
                                        name: 'offset',
                                        options: {
                                            offset: [-9, 5], // [skid, distance] => shift 9px left
                                        },
                                    },
                                ],
                            }}
                        >
                            <span
                                onClick={() =>
                                    scheduleDraftsData?.draft_edit_url &&
                                    typeof window !== 'undefined' &&
                                    window.open(scheduleDraftsData?.draft_edit_url, '_blank')
                                }
                                className="schedule_edit"
                                tabIndex={0}
                                style={{ cursor: 'pointer' }}
                            >
                                <FiEdit size={18} className="schedule_edit_icon " />
                            </span>
                        </OverlayTrigger>

                        <span onClick={() => props.setOpen(null)} className="schedule_cross" style={{ cursor: 'pointer' }}>
                            <IoCloseOutline size={18} />
                        </span>
                    </div>

                    <div className='schedule_popup_head'>
                        {props?.open?.content_title}
                    </div>
                    <div className='schedule_subhead_container'>
                        {props.open?.author && <span className='schedule_popup_subhead'>Author: {props.open?.author}</span>}
                        <span style={{ borderRight: '1px solid #e6e6e6', margin: '0px 16px' }} />
                        {props.open?.content_type && <span className='schedule_popup_subhead'> Type: {props.open?.content_type}</span>}
                    </div>
                    <Form>

                        <Row><div className='schedule_publication'>Publication Schedule</div></Row>
                        <Row>
                            <Col>
                                <Form.Group className="mb-3 date_input scheduler_date_picker" id="date_input" controlId="startDate">
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DesktopDatePicker
                                            label="Custom input"
                                            value={customDate}
                                            onChange={handleDateChange}
                                            PopperProps={{
                                                placement: "bottom-start",
                                                modifiers: [
                                                    {
                                                        name: "offset",
                                                        options: {
                                                            offset: [0, 6],
                                                        },
                                                    },
                                                ],
                                            }}
                                            minDate={new Date()}
                                            inputFormat="EEEE, d MMMM"
                                            onOpen={() => setDateOpen(true)}
                                            onClose={() => setDateOpen(false)}
                                            renderInput={({ inputRef, inputProps, InputProps }) => (
                                                <Box sx={{ display: "flex", alignItems: "center", marginBottom: "6px" }}>
                                                    <Form.Control
                                                        ref={inputRef}
                                                        {...inputProps}
                                                        className="input_pd"
                                                        placeholder={format(new Date(), "EEEE, d MMMM")}
                                                        onClick={InputProps?.endAdornment?.props?.children?.props?.onClick}
                                                        readOnly
                                                    />
                                                    {/* Replace the default icon with LuCalendar */}
                                                    {InputProps?.endAdornment && (
                                                        <span
                                                            onClick={InputProps?.endAdornment?.props?.children?.props?.onClick}
                                                            style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
                                                        >
                                                            <LuCalendar size={18} style={{ position: 'absolute', left: '12px', color: '#606060' }} />
                                                        </span>
                                                    )}
                                                    <div onClick={InputProps?.endAdornment?.props?.children?.props?.onClick}>
                                                        {dateOpen ? (
                                                            <IoIosArrowUp className="date_down" size={16} />
                                                        ) : (
                                                            <IoIosArrowDown className="date_down" size={16} />
                                                        )}
                                                    </div>
                                                </Box>
                                            )}
                                        />
                                        <div style={{ color: "red", fontSize: "12px", height: '10px' }}>
                                            {errors?.customDate && errors?.customDate}
                                        </div>
                                    </LocalizationProvider>
                                </Form.Group>
                            </Col>
                            <Col>
                                <Form.Group className="mb-3 time_input" controlId="startTime">
                                    <CreatableSelect
                                        ref={selectRef}
                                        className="input_pd_1"
                                        value={customTime}
                                        onChange={handleTimeChange}
                                        options={timeOptions}
                                        placeholder="Select Time"
                                        components={{
                                            Control: (props) => <CustomControl {...props} type="time" />,
                                        }}
                                        styles={customStyles}
                                        isClearable={false}
                                        formatCreateLabel={(inputValue) => `Use custom time: "${inputValue}"`}
                                    />

                                    <div style={{ color: "red", fontSize: "12px", height: '10px' }}>
                                        {errors?.customTime && errors.customTime}
                                    </div>

                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <div className='schedule_publication' style={{ paddingTop: '10px' }}>
                                Add Notification Recipients
                                <div style={{ color: '#7a7a7a', fontSize: '10px' }}>
                                    <LuInfo style={{ marginRight: '4px' }} />
                                    Author and scheduler will be notified by default. Add others to be notified about this post.
                                </div>
                            </div>
                        </Row>
                        <Row>
                            <Col>
                                <CustomDropdown
                                    type="user"
                                    selectedValues={users}
                                    onChange={setUsers}
                                    userLimit={userLimit}
                                />
                                <div style={{ color: "red", fontSize: "12px", height: '10px', }}>
                                    {errors?.user && <span style={{ padding: '5px 5px 16px 0px' }}>{errors?.user}</span>}
                                </div>

                                {/* Display selected users as chips below the select */}
                                {users && users.length > 0 && (
                                    <div style={{ marginTop: "10px", display: "flex", flexWrap: "wrap", gap: "6px", }}>
                                        {visibleUsers?.map((user, index) => {
                                            const displayName =
                                                user?.name?.charAt(0).toUpperCase() +
                                                user?.name?.slice(1) || user?.user__name ||
                                                user?.email?.split("@")[0] ||
                                                "Unknown User";

                                            return (
                                                <div key={index} style={{ display: "flex", alignItems: "center", backgroundColor: "#e3f2fd", borderRadius: "16px", padding: "6px 14px", fontSize: "12px", color: "#1976d2", }}>
                                                    <span>{displayName}</span>
                                                    <span
                                                        onClick={() => {
                                                            const updatedUsers = users.filter((_, i) => i !== index);
                                                            setUsers(updatedUsers);
                                                        }}
                                                        style={{ cursor: "pointer", margin: "1px 0px 0px 6px", fontSize: "14px", fontWeight: "bold", }}
                                                    >
                                                        ×
                                                    </span>
                                                </div>
                                            );
                                        })}

                                        {/* Show +N counter if there are more than 3 users */}
                                        {!showAll && hiddenCount > 0 && (
                                            <div
                                                onClick={() => setShowAll(true)}
                                                style={{ display: "flex", alignItems: "center", backgroundColor: "#e3f2fd", borderRadius: "16px", padding: "6px 14px", fontSize: "12px", color: "#1976d2", cursor: "pointer", }}
                                            >
                                                +{hiddenCount}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </Col>
                        </Row>

                        {/* to show error message if PUT API gets failed */}

                        <Row>
                            <Col>
                                {putScheduleDraftsError && (
                                    <div style={{ marginTop: '10px' }}>
                                        <Alert severity={putScheduleDraftsError && 'error'}>{putScheduleDraftsError?.message}</Alert>
                                    </div>
                                )}
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <div className='d-flex justify-content-end' style={{ marginTop: '20px' }}>
                                    <div style={{ color: '#d23333', padding: '7px 16px', marginRight: '20px', cursor: 'pointer' }}
                                        onClick={() => setToBeRemoved(true)}>
                                        Remove
                                    </div>
                                    <button
                                        type='button'
                                        className="reschedule"
                                        onClick={handleSchedule}
                                        disabled={isScheduleDisabled}
                                    >
                                        Set Schedule
                                    </button>
                                </div>
                            </Col>
                        </Row>
                    </Form>
                </div>
            </CommonModal>


            {/* confirmation popup for removing scheduled draft */}
            <CommonModal
                isOpen={toBeRemoved}
                closeModal={() => {
                    setToBeRemoved(false);
                }}
                showCloseIcon={false}
                closeOnOverlayClick={false}
            >
                <div className="delete_confirmation">
                    <div className="text_edit"> Are you sure want to delete ?</div>
                    <div className="icon_buttons">
                        <button className="icon_edits icon_yes_color" onClick={handleRemove}>
                            Yes
                        </button>
                        <button className="icon_edits icon_no_color" onClick={() => setToBeRemoved(false)}>
                            No
                        </button>
                    </div>
                </div>
            </CommonModal>
        </>
    )
}

export default SetSchedulePopup